import { FaroCoreApiTypes } from "@stellar/core-api-client";

type AdmUserRole = FaroCoreApiTypes.AdmUserRole;

/** Roles that may administer the platform (everything except read-only access) */
const ADMINS = ["administrator", "developer"] as const;

/** Roles that may look at data without changing it */
const READERS = ["adminRead", ...ADMINS] as const;

/**
 * Which roles grant which UI action, grouped by what the action works on.
 * A user holding several roles gets the union of what each role grants.
 * `apiDocsReader` is intentionally not listed: it only grants access to the API docs.
 *
 * To change who can do something, edit the role list here. To add an action, add a key and use
 * it through `usePermission` or `RequirePermission`.
 *
 * NOTE: This only drives the UI. The backend must still enforce the roles.
 */
export const PERMISSIONS = {
  // Actions that work the same on every kind of entity
  general: {
    openInLegacyAdmin: READERS,
    copyDetailsToClipboard: READERS,
  },

  member: {
    edit: ADMINS,
    impersonate: ADMINS,
    resetPassword: ADMINS,
    changeAuthentication: ADMINS,
    revokeSessions: ADMINS,
    deleteMember: ADMINS,
    invite: ADMINS,
    resendInvitation: ADMINS,
    removeFromWorkspace: ADMINS,
  },

  project: {
    edit: ADMINS,
    clone: ADMINS,
    changeStandalone: ADMINS,
    addPlan: ADMINS,
    editPlan: ADMINS,
  },

  workspace: {
    create: ADMINS,
    changeType: ADMINS,
    edit: ADMINS,
    impersonate: ADMINS,
    deleteWorkspace: ADMINS,
    createProject: ADMINS,
    addPlan: ADMINS,
    editPlan: ADMINS,
  },
} as const satisfies Record<string, Record<string, readonly AdmUserRole[]>>;

/** Every permission as a "domain.action" string, e.g. "workspace.deleteWorkspace" */
export type PermissionKey = {
  [
    Domain in keyof typeof PERMISSIONS
  ]: `${Domain}.${keyof (typeof PERMISSIONS)[Domain] & string}`;
}[keyof typeof PERMISSIONS];
